import React from 'react';
import { logger } from 'src/commons/utils/logger';
import Mount from './Mount';
import Remote, { AfterLoadProps } from './Remote';

/*
 * The context object the bell drawer needs alongside its display config. Shape
 * taken from the sole caller, DynamicCommunicationsDrawer.
 */
export interface CommunicationsDrawerConsiliorContext {
  entityId?: string;
  marketplaceId?: string;
  locale?: string;
  isImpersonator: boolean;
  advertiserId?: string;
  advertiserType?: string;
  domain: string;
  realm: string;
}

/*
 * Was `Record<string, unknown>`, which made every field unreadable at the point of
 * use -- consumers had to cast to get at `enableCardV2`. Named for real so the
 * forwarded config is checked at the call site instead.
 */
export interface CommunicationsDrawerConfigs {
  variant: string;
  enableCardV2: boolean;
  // Forwarded straight through to the remote, which owns the client's contract.
  advertisingApiClient: unknown;
  consiliorContext: CommunicationsDrawerConsiliorContext;
}

// Props are forwarded wholesale to the federated bell-drawer via Mount's
// `mountProps`; this is the shape the sole caller (DynamicCommunicationsDrawer)
// passes. Exported so test mocks of this component can reuse it rather than each
// re-declaring its own approximation.
export interface FederatedModuleProps {
  configs: CommunicationsDrawerConfigs;
  srLabel: string;
  entityId: string;
  isOpen: boolean;
  onNewNotifications: (hasNewNotifications: boolean) => void;
  triggerComponent: React.RefObject<HTMLElement>;
}

// The dynamically imported bell-drawer module exposes a `mount` entry point;
// `mount` is the only member this component consumes from the loaded module.
interface LoadedCommunicationsDrawerModule {
  mount: (element: HTMLElement | null, props: unknown) => void;
}

// The import can RESOLVE to a module without `mount`: the remote's
// duplicate-init runtime plugin falls back to an empty module
// (`{ __esModule: true, default: {} }`) when it cannot recover. Calling that
// missing `mount` threw inside Mount's effect, and the drawer's error boundary
// logged it as "<x> is not a function" -- about 430 sessions a day in prod.
const isMountable = (
  loaded: unknown,
): loaded is LoadedCommunicationsDrawerModule =>
  typeof (loaded as Partial<LoadedCommunicationsDrawerModule> | undefined)
    ?.mount === 'function';

const loader = () =>
  import('communicationsDrawerAssets/AcesCommunicationsDrawer');

const FederatedModule = (props: FederatedModuleProps) => {
  const remoteProps = {
    loader,
    timeout: 10000,
    // Runs once per load, unlike render, so the failure is logged once.
    afterLoad: ({ module }: AfterLoadProps) => {
      if (!isMountable(module)) {
        logger.error(
          'Communications drawer module loaded without a mount function',
          {
            // Which exports it did have, e.g. ['__esModule', 'default'] for
            // the remote's empty fallback module.
            exportedKeys:
              module && typeof module === 'object' ? Object.keys(module) : [],
          },
        );
      }
    },
    render: (loaded: unknown) => {
      // A failed or timed-out load renders nothing; Remote logs the failure.
      if (!isMountable(loaded)) return <></>;
      const { mount } = loaded;

      return (
        <Mount containerId="bell-drawer" mount={mount} mountProps={props} />
      );
    },
  };

  return <Remote {...remoteProps} />;
};

export default FederatedModule;
